/**
 * payment-method.ts — F1d async PAYMENT_METHOD source.
 *
 * Exports:
 *   hasPaymentMethodCondition(logic): boolean
 *     Lazy-gate: returns true if any Rule in logic contains a
 *     PAYMENT_METHOD condition. Used to decide whether to load Stripe.js.
 *
 *   probePaymentMethod(publishableKey?): Promise<string[]>
 *     Lazy-loads Stripe.js from CDN, creates a paymentRequest probe,
 *     calls canMakePayment(), returns ['apple_pay'] / ['google_pay'] / [].
 *     Fails open on any error (returns []).
 *
 * [verify-at-code] — Stripe.js paymentRequest params:
 *   country, currency, total are required for canMakePayment() per Stripe docs.
 *   Probe uses: country='US', currency='usd', total.amount=1 (1 cent = $0.01).
 *   These are capability-probe params only — no real charge is made.
 *
 * The caller MUST pass `publishableKey` — this module does NOT read any env.
 * WEB-183 wired both call sites (PublicQuiz.tsx, PublicPaywall.tsx) to pass
 * `STRIPE_PUBLISHABLE_KEY` from `@/config/stripe-pk` (Vite `VITE_STRIPE_
 * PUBLISHABLE_KEY`). When that env is UNSET the key is undefined → returns []
 * → PAYMENT_METHOD conditions evaluate FALSE (graceful). Set the platform
 * publishable key in the build env to activate device-capability targeting.
 * The probe checks device capability only, so a platform key suffices (the
 * per-project Connect key used for checkout is not required here).
 *
 * Note: @stripe/stripe-js is NOT a project dependency. Stripe.js is loaded
 * lazily from CDN (js.stripe.com/v3). In test environments, window.Stripe
 * is stubbed directly in test setup. This avoids adding Stripe as a bundle
 * dependency for users who never trigger PAYMENT_METHOD conditions.
 */

import type { Logic } from '@/logic-engine';

// ── Internal type helpers ──────────────────────────────────────────────────

/** Minimal Stripe PaymentRequest result shape. */
interface CanMakePaymentResult {
  applePay?: boolean;
  googlePay?: boolean;
  [key: string]: boolean | undefined;
}

interface StripePaymentRequest {
  canMakePayment(): Promise<CanMakePaymentResult | null>;
}

interface StripeInstance {
  paymentRequest(options: {
    country: string;
    currency: string;
    total: { label: string; amount: number };
    requestPayerEmail?: boolean;
  }): StripePaymentRequest;
}

type StripeConstructor = (publishableKey: string) => StripeInstance;

// ── Group traversal helper ────────────────────────────────────────────────

/**
 * groupHasPaymentSource — recursively checks a condition_group.items array
 * for any Condition with source === 'PAYMENT_METHOD'.
 *
 * Conditions have a `source` field; Groups have `combinator` + `items`
 * (per rule.schema.ts — combinator is 'ALL' | 'ANY').
 */
function groupHasPaymentSource(items: unknown[]): boolean {
  for (const item of items) {
    const it = item as Record<string, unknown>;
    if ('source' in it) {
      // It's a Condition
      if (it['source'] === 'PAYMENT_METHOD') return true;
    } else if ('combinator' in it && Array.isArray(it['items'])) {
      // It's a nested Group
      if (groupHasPaymentSource(it['items'] as unknown[])) return true;
    }
  }
  return false;
}

// ── Public API ────────────────────────────────────────────────────────────

/**
 * hasPaymentMethodCondition — lazy-gate check.
 *
 * Returns true if any Rule in logic contains a PAYMENT_METHOD-source
 * Condition at any nesting level. Used to decide whether to probe Stripe.
 */
export function hasPaymentMethodCondition(logic: Logic): boolean {
  for (const rule of logic) {
    if (groupHasPaymentSource(rule.condition_group.items as unknown[])) {
      return true;
    }
  }
  return false;
}

/**
 * WEB-1336 (П1): сторож по времени на оба внешних ожидания (загрузка
 * js.stripe.com и canMakePayment). 5 секунд — ориентир из карточки: на
 * медленной мобильной сети слишком короткий сторож превратил бы «Apple Pay
 * доступен» в «недоступен», но мягкий отказ безопаснее вечного индикатора
 * на экране покупателя. Экспортировано для тестов.
 */
export const PAYMENT_PROBE_TIMEOUT_MS = 5_000;

/** Resolve `fallback` if `promise` has not settled within `ms`. */
function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(fallback);
      },
    );
  });
}

/**
 * loadStripeGlobal — lazy-load Stripe.js from CDN.
 *
 * Returns window.Stripe constructor, or null if:
 *   - Running in SSR (no window)
 *   - CDN load fails
 *   - Script already exists but Stripe still not on window
 *
 * Idempotent: injects the script tag only once.
 *
 * WEB-1336 (П1): the "script already injected" branch used to listen ONLY for
 * `load` — if that earlier script had already FAILED, `load` would never fire
 * and the promise stayed pending forever, freezing the first screen behind
 * the payment-probe gate (the doc above promised "fails open on any error"
 * and lied for this path). Now the branch also listens for `error` and every
 * path is capped by PAYMENT_PROBE_TIMEOUT_MS in probePaymentMethod.
 */
async function loadStripeGlobal(): Promise<StripeConstructor | null> {
  if (typeof window === 'undefined') return null;

  const w = window as unknown as Record<string, unknown>;

  // Already loaded
  if (typeof w['Stripe'] === 'function') {
    return w['Stripe'] as StripeConstructor;
  }

  // Inject script tag (lazy)
  return new Promise<StripeConstructor | null>((resolve) => {
    const STRIPE_CDN = 'https://js.stripe.com/v3/';

    // Script already injected (e.g., by another component) — wait for it
    const existing = document.querySelector(`script[src="${STRIPE_CDN}"]`);
    if (existing) {
      const settle = () => {
        resolve((w['Stripe'] as StripeConstructor) ?? null);
      };
      existing.addEventListener('load', settle, { once: true });
      // WEB-1336: a script that already failed (or fails later) never fires
      // `load` — soft-fail instead of hanging forever.
      existing.addEventListener('error', settle, { once: true });
      // If already loaded (readyState check not available on HTMLScriptElement)
      // trigger immediately if Stripe appeared between check and listener
      if (typeof w['Stripe'] === 'function') {
        existing.removeEventListener('load', settle);
        existing.removeEventListener('error', settle);
        resolve(w['Stripe'] as StripeConstructor);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = STRIPE_CDN;
    script.onload = () => resolve((w['Stripe'] as StripeConstructor) ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
}

/**
 * probePaymentMethod — async probe for available payment methods.
 *
 * Steps:
 *   1. Require publishable key (caller-passed). Return [] if absent (prod gap).
 *   2. Lazy-load Stripe.js from CDN.
 *   3. Create paymentRequest with capability-probe params.
 *   4. Call canMakePayment() → extract apple_pay / google_pay.
 *   5. Fail open: any error → [].
 *
 * @param publishableKey  Stripe publishable key, passed by the caller. No env
 *   fallback (see PROD GAP note at the top). Absent → returns [].
 */
export async function probePaymentMethod(publishableKey?: string): Promise<string[]> {
  // publishableKey must be passed by the caller (QuizScreenWizard reads Vite env
  // at the call site via import.meta.env). Keeping this module free of import.meta
  // ensures it compiles cleanly in jest/CommonJS test environments.
  const pk = publishableKey;

  if (!pk) return [];

  try {
    // WEB-1336 (П1): both external waits are capped — a stuck CDN load or a
    // never-resolving canMakePayment() soft-fails to "no wallet" instead of
    // freezing the first screen forever.
    const StripeConstructor = await withTimeout(
      loadStripeGlobal(),
      PAYMENT_PROBE_TIMEOUT_MS,
      null,
    );
    if (!StripeConstructor) return [];

    const stripe = StripeConstructor(pk);
    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'Payment capability check', amount: 1 },
    });

    const result = await withTimeout(
      pr.canMakePayment(),
      PAYMENT_PROBE_TIMEOUT_MS,
      null,
    );
    if (!result) return [];

    const methods: string[] = [];
    if (result.applePay) methods.push('apple_pay');
    if (result.googlePay) methods.push('google_pay');
    return methods;
  } catch {
    return []; // fail-open
  }
}
